import React from 'react';
import {
  Box, Button, Card, CardContent, CardHeader, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, LinearProgress,
  TextField, Tooltip, Typography, Switch, FormControlLabel,
  Divider
} from '@mui/material';
import Alert from '@mui/material/Alert';
import BasicTable2 from '../tools/BasicTable2';
import * as ETVConstants from '../ETVConstants';
import FloorWayfindingEditor from './FloorWayfindingEditor';
import {
  Delete, Edit, Save, CheckCircle, Block, TvOutlined, Add, ImageOutlined,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import ConnectFloorWithDisplays from './ConnectFloorWithDisplays';
import FloorDirectoryImageCreator from './FloorDirectoryImageCreator';

class FloorMgmt extends React.Component {
  state = {
    items: [],
    isLoaded: false,
    isLoading: false,
    error: null,
    floor_nodes:[],

    window: '',
    info_title: '',
    info_message: '',

    action: '',
    floor_id: undefined,
    floor_label: '',
    floor_info: '',             // full string sent to backend (derived)
    floor_pos: 0,
    floor_info_entries: [],     // array for UI chips
    new_entry_text: '',         // input for adding a chip
    public_access: false,
    main_entry: false,

    // hidden position (not shown in UI, but sent on update to keep backend happy)
    floor_pos: 0,

    allDevices: [],
    selected_floor_id: undefined,
  };

  componentDidMount() {
    this.loadFloors();
    this.loadAllDisplays();
  }

  componentDidUpdate() {
    if (!this.state.isLoaded && !this.state.isLoading) {
      this.loadFloors();
    }
  }

  // ---------------- data loading ----------------
  loadAllDisplays() {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_devices_by_location&location_id=${this.props.location_id}`;
    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(
        (result) => this.setState({ allDevices: result.list || [] }),
        (error) => this.setState({ allDevices: [], error })
      );
  }

  loadFloors = () => {
    this.setState({ isLoading: true, error: null });
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_floors&location_id=${this.props.location_id}`;
    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(
        (result) => {
          this.setState({
            items: result.list || [],
            isLoaded: true,
            isLoading: false,
          });
        },
        (error) => {
          this.setState({
            items: [],
            isLoaded: false,
            isLoading: false,
            error,
          });
        }
      );
  };

  parseFloorNodes = (floorInfo) => {
    const parsed =
      typeof floorInfo === 'string'
        ? JSON.parse(floorInfo || '{}')
        : floorInfo;

    return Array.isArray(parsed.nodes) ? parsed.nodes : [];
  };

  buildFloorInfo = () => {
    return JSON.stringify({
      nodes: this.state.floor_nodes || []
    });
  };
  
  // ---------------- helpers ----------------
  getFloorLabel(floorID) {
    const id = String(floorID);
    const f = this.state.items.find(x => String(x.floor_id) === id);
    return f ? f.floor_label : '';
  }

  showWindow = (id, show) => this.setState({ window: show ? id : '' });
  showInfoMessage = (title, msg) => this.setState({ window: 'message_div', info_title: title, info_message: msg });
  closeInfoMessage = () => this.setState({ window: '', info_message: '' });

  // Small helper to POST urlencoded
  postForm = (url, params) => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const body = new URLSearchParams(params);
    return fetch(url, {
      method: 'POST',
      headers: {
        'user_id': reqUserID,
        'access_token': accessToken,
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body,
    }).then(res => res.json());
  };

  // ---------------- floor CRUD ----------------
  addFloor = () => {
    this.setState({
      window: 'add_floor_div',
      action: 'add_floor',
      floor_id: undefined,
      floor_label: '',
      floor_info: '',
      floor_info_entries: [],
      floor_nodes:[],
      new_entry_text: '',
      public_access: false,
      main_entry: false,
      floor_pos: 0, // will compute next in save
      info_title: ETVConstants.trans('add_floor'),
    });
  };

  editFloor = (floor_id) => {
    const id = String(floor_id);
    const floor = this.state.items.find(f => String(f.floor_id) === id);
    if (!floor) return;

    const nodes = this.parseFloorNodes(floor.floor_info);

    this.setState({
      window: 'add_floor_div',
      action: 'update_floor',
      floor_id: floor.floor_id,
      floor_label: floor.floor_label || '',
      floor_info: floor.floor_info || '',
      floor_nodes: nodes,
      public_access: !!floor.public_access,
      main_entry: !!floor.main_entry,
      floor_pos: Number(floor.floor_pos ?? 0),
      info_title: ETVConstants.trans('edit_floor'),
    });
  };

  saveFloor = () => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const { floor_label, floor_info_entries, public_access, main_entry, items } = this.state;
    const floor_info = this.buildFloorInfo();
    //const floor_info = floor_info_entries.map(s => s.trim()).filter(Boolean).join('; ');
    const maxPos = Math.max(0, ...items.map(f => Number(f.floor_pos) || 0));
    const nextPos = maxPos + 1; // always append
    
    const url = `${ETVConstants.getServerBase()}/floor_access?action=add_floor`
      + `&location_id=${this.props.location_id}`
      + `&floor_label=${encodeURIComponent(floor_label)}`
      + `&floor_info=${encodeURIComponent(floor_info)}`
      + `&floor_pos=${nextPos}`                     // required by backend
      + `&public_access=${public_access}`
      + `&main_entry=${main_entry}`;          // "true"/"false"

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(() => this.setState({ window: '', isLoaded: false }))
      .catch(() => this.showInfoMessage('Error', 'Failed to save floor'));
  };

  updateFloor = () => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const { floor_id, floor_label, floor_info_entries, public_access, main_entry, floor_pos } = this.state;

    //const floor_info = floor_info_entries.map(s => s.trim()).filter(Boolean).join('; ');
    const floor_info = this.buildFloorInfo();

    const url = `${ETVConstants.getServerBase()}/floor_access?action=update_floor`
      + `&floor_id=${floor_id}`
      + `&floor_label=${encodeURIComponent(floor_label)}`
      + `&floor_info=${encodeURIComponent(floor_info)}`
      + `&floor_pos=${floor_pos}`                   // pass through unchanged
      + `&public_access=${public_access}`
      + `&main_entry=${main_entry}`;          // "true"/"false"

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(() => this.setState({ window: '', isLoaded: false }))
      .catch(() => this.showInfoMessage('Error', 'Failed to save floor'));
  };


  deleteFloor = (floorID) => {
    const url = `${ETVConstants.getServerBase()}/floor_access?action=delete_floor`;
    const params = { floor_id: floorID };

    this.postForm(url, params)
      .then(() => this.setState({ window: '', isLoaded: false }))
      .catch(() => this.showInfoMessage('Error', 'Failed to delete floor'));
  };

  editServingDisplays = (floorID) => {
    this.setState({ selected_floor_id: floorID, window: 'edit_serving_displays' });
  };

  // ---------------- info entries helpers ----------------
  addInfoEntry = () => {
    const t = (this.state.new_entry_text || '').trim();
    if (!t) return;
    this.setState((s) => ({
      floor_info_entries: [...s.floor_info_entries, t],
      new_entry_text: ''
    }));
  };

  removeInfoEntry = (idx) => {
    this.setState((s) => ({
      floor_info_entries: s.floor_info_entries.filter((_, i) => i !== idx)
    }));
  };

  handleEntryKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.addInfoEntry();
    }
  };

  moveFloor = (floorID, direction) => {
    const sorted = [...this.state.items].sort(
      (a, b) => Number(a.floor_pos) - Number(b.floor_pos)
    );

    const index = sorted.findIndex(
      f => String(f.floor_id) === String(floorID)
    );

    const targetIndex = index + direction;

    // out of range (top moving up / bottom moving down)
    if (
      index < 0 ||
      targetIndex < 0 ||
      targetIndex >= sorted.length
    ) {
      return;
    }

    const current = sorted[index];
    const target = sorted[targetIndex];

    const currentPos = current.floor_pos;
    const targetPos = target.floor_pos;

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');

    const updatePosition = (floor, newPos) => {
      const floorInfo =
        typeof floor.floor_info === 'string'
          ? floor.floor_info
          : JSON.stringify(floor.floor_info);

      const url =
        `${ETVConstants.getServerBase()}/floor_access?action=update_floor`
        + `&floor_id=${floor.floor_id}`
        + `&floor_label=${encodeURIComponent(floor.floor_label)}`
        + `&floor_info=${encodeURIComponent(floorInfo)}`
        + `&floor_pos=${newPos}`
        + `&public_access=${floor.public_access}`
        + `&main_entry=${floor.main_entry}`;

      return fetch(url, {
        headers: {
          user_id: reqUserID,
          access_token: accessToken
        }
      }).then(res => res.json());
    };

    // swap positions
    Promise.all([
      updatePosition(current, targetPos),
      updatePosition(target, currentPos)
    ])
    .then(() => {
      this.setState({ isLoaded: false });
    })
    .catch(() => {
      this.showInfoMessage(
        'Error',
        'Failed to move floor'
      );
    });
  };


  // ---------------- render ----------------
  render() {
    const { items, isLoading, error } = this.state;

    const columnsDataFloor = [
      {
        title: '',
        field: '',
        render: rd => {
          const sorted = [...this.state.items].sort(
            (a, b) => Number(a.floor_pos) - Number(b.floor_pos)
          );

          const index = sorted.findIndex(
            f => String(f.floor_id) === String(rd.floor_id)
          );

          const isTop = index === 0;
          const isBottom = index === sorted.length - 1;

          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                disabled={isTop}
                onClick={() => this.moveFloor(rd.floor_id, -1)}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                disabled={isBottom}
                onClick={() => this.moveFloor(rd.floor_id, 1)}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
            </Box>
          );
        }
      },
      { title: ETVConstants.trans('floor_label'), field: 'floor_label' },
      {
        title: ETVConstants.trans('floor_info'),
        field: 'floor_info',
        render: rd => {
           const parsed =
            typeof rd.floor_info === 'string'
              ? JSON.parse(rd.floor_info || '{}')
              : rd.floor_info;

          return (parsed.nodes || [])
            .map(n => n.label)
            .filter(Boolean)
            .join(', ');
        }
      },
      {
        title: ETVConstants.trans('public_access'),
        field: 'public_access',
        render: rd => (
          rd.public_access
            ? <Chip size="small" color="success" icon={<CheckCircle />} label={ETVConstants.trans('public')} />
            : <Chip size="small" color="default" icon={<Block />} label={ETVConstants.trans('private')} />
        )
      },
      {
        title: ETVConstants.trans('main_entry'),
        field: 'main_entry',
        render: rd => (
          rd.main_entry
            ? <Chip size="small" color="success" icon={<CheckCircle />} label={ETVConstants.trans('main_entry')} />
            : <span/>
        )
      },
      {
        title: ETVConstants.trans('edit'),
        field: '',
        render: rd => (
          <Button size="small" variant="contained" startIcon={<Edit />} onClick={() => this.editFloor(rd.floor_id)}>
            {ETVConstants.trans('edit')}
          </Button>
        )
      },
      {
        title: ETVConstants.trans('serving_displays'),
        field: '',
        render: rd => (
          <Button size="small" variant="contained" color="secondary" startIcon={<TvOutlined />} onClick={() => this.editServingDisplays(rd.floor_id)}>
            {ETVConstants.trans('serving_displays')}
          </Button>
        )
      },
      {
        title: ETVConstants.trans('delete'),
        field: '',
        render: rd => (
          <Tooltip title={ETVConstants.trans('delete')}>
            <IconButton color="error" size="small" onClick={() => this.deleteFloor(rd.floor_id)}>
              <Delete />
            </IconButton>
          </Tooltip>
        )
      },
    ];

    return (
      <Box sx={{ width: 'min(1100px, 100%)' }}>
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <CardHeader
            title={<Typography variant="h6">{ETVConstants.trans('floors') || 'Floors'}</Typography>}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="medium" variant="contained" startIcon={<Add />} onClick={this.addFloor}>
                  {ETVConstants.trans('add_floor')}
                </Button>
                <Button
                  size="medium"
                  variant="contained"
                  startIcon={<ImageOutlined />}
                  onClick={() => this.showWindow('directory_image_creation', true)}
                >
                  {ETVConstants.trans('create_directory_image') || 'Create Directory image'}
                </Button>
              </Box>
            }
            sx={(t) => ({
              px: 2,
              backgroundColor: t.palette.action.hover,
              borderBottom: `1px solid ${t.palette.divider}`,
              '& .MuiCardHeader-content': { display: 'flex', alignItems: 'center' },
            })}
          />

          {isLoading && <LinearProgress />}

          <CardContent sx={{ p: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {ETVConstants.trans('error') || 'Error'}: {String(error)}
              </Alert>
            )}

            <BasicTable2 data={items} columns={columnsDataFloor} title="" pageSize={50} allow_search />
          </CardContent>
        </Card>

        {/* Info dialog */}
        <Dialog open={this.state.window === 'message_div'} onClose={this.closeInfoMessage} maxWidth="xs" fullWidth>
          <DialogTitle>{this.state.info_title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Typography variant="body1" component="span">{this.state.info_message}</Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeInfoMessage}>{ETVConstants.trans('close')}</Button>
          </DialogActions>
        </Dialog>

        {/* Serving displays dialog */}
        <Dialog open={this.state.window === 'edit_serving_displays'} onClose={() => this.showWindow('', false)} maxWidth="md" fullWidth>
          <DialogTitle>{ETVConstants.trans('serving_displays')}</DialogTitle>
          <DialogContent dividers>
            {this.state.selected_floor_id ? (
              <ConnectFloorWithDisplays
                floor_label={this.getFloorLabel(this.state.selected_floor_id)}
                floor_id={this.state.selected_floor_id}
                all_devices={this.state.allDevices}
              />
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.showWindow('', false)}>{ETVConstants.trans('close')}</Button>
          </DialogActions>
        </Dialog>

        {/* Floor directory image creation dialog */}
        <Dialog
          open={this.state.window === 'directory_image_creation'}
          onClose={() => this.showWindow('', false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{ETVConstants.trans('create_directory_image') || 'Create directory image'}</DialogTitle>
          <DialogContent dividers>
            <FloorDirectoryImageCreator floors={this.state.items} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.showWindow('', false)}>{ETVConstants.trans('close')}</Button>
          </DialogActions>
        </Dialog>

        {/* ADD / EDIT FLOOR dialog */}
        <Dialog
          open={this.state.window === 'add_floor_div'}
          onClose={() => this.showWindow('', false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{this.state.info_title || ETVConstants.trans('edit_floor')}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              {/* Floor label */}
              <Typography variant="body2" color="text.secondary">
                {ETVConstants.trans('desc_floor_label')}
              </Typography>
              <TextField
                label={ETVConstants.trans('floor_label')}
                fullWidth
                value={this.state.floor_label}
                onChange={(e) => this.setState({ floor_label: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(this.state.public_access)}
                      onChange={(e) => this.setState({ public_access: e.target.checked })}
                    />
                  }
                  label={ETVConstants.trans('public_access')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(this.state.main_entry)}
                      onChange={(e) => this.setState({ main_entry: e.target.checked })}
                    />
                  }
                  label={ETVConstants.trans('main_entry') || 'Main entry'}
                />
              </Box>
              <Divider/>
              <Box sx={{ display: 'flex', gap: 1, marginTop:1 }}>
                <FloorWayfindingEditor
                  nodes={this.state.floor_nodes}
                  onChange={(nodes) => this.setState({ floor_nodes: nodes })}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.showWindow('', false)}>
              {ETVConstants.trans('close')}
            </Button>
            {this.state.action === 'add_floor' ? (
              <Button startIcon={<Save />} variant="contained" onClick={this.saveFloor}>
                {ETVConstants.trans('save')}
              </Button>
            ) : (
              <Button startIcon={<Save />} variant="contained" onClick={this.updateFloor}>
                {ETVConstants.trans('update')}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default FloorMgmt;
